import { useState, useEffect } from 'react';
import { Users, Check, X, Loader2, Search, Filter, Trash2, AppWindow, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input } from '../components/ui';
import { getAllUsers, approveUser, revokeUser, deleteUser, updateUserAllowedApps } from '../services/userService';
import { User } from '../types';
import './AdminUsersPage.css';

// Available apps in the system
const AVAILABLE_APPS = [
    { id: 'image-generator', name: 'Générateur d\'Images AI', icon: '🖼️' },
    // Add more apps here as they are created
];

export function AdminUsersPage() {
    const { userProfile } = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('all');
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        setIsLoading(true);
        setError('');
        try {
            const data = await getAllUsers();
            setUsers(data);
        } catch (err: any) {
            console.error('Error loading users:', err);
            setError('Erreur lors du chargement des utilisateurs');
        } finally {
            setIsLoading(false);
        }
    };

    const handleApprove = async (userId: string) => {
        setProcessingId(userId);
        try {
            await approveUser(userId);
            setUsers(users.map(u => u.uid === userId ? { ...u, isApproved: true } : u));
        } catch (err: any) {
            console.error('Error approving user:', err);
            setError('Erreur lors de l\'approbation');
        } finally {
            setProcessingId(null);
        }
    };

    const handleRevoke = async (userId: string) => {
        setProcessingId(userId);
        try {
            await revokeUser(userId);
            setUsers(users.map(u => u.uid === userId ? { ...u, isApproved: false } : u));
        } catch (err: any) {
            console.error('Error revoking user:', err);
            setError('Erreur lors de la révocation');
        } finally {
            setProcessingId(null);
        }
    };

    const handleDelete = async (userId: string) => {
        setProcessingId(userId);
        setDeleteConfirmId(null);
        try {
            await deleteUser(userId);
            setUsers(users.filter(u => u.uid !== userId));
        } catch (err: any) {
            console.error('Error deleting user:', err);
            setError('Erreur lors de la suppression de l\'utilisateur');
        } finally {
            setProcessingId(null);
        }
    };

    const handleToggleApp = async (userId: string, appId: string) => {
        const user = users.find(u => u.uid === userId);
        if (!user) return;

        const currentApps = user.allowedApps || [];
        const newApps = currentApps.includes(appId)
            ? currentApps.filter(id => id !== appId)
            : [...currentApps, appId];

        setProcessingId(userId);
        try {
            await updateUserAllowedApps(userId, newApps);
            setUsers(users.map(u => u.uid === userId ? { ...u, allowedApps: newApps } : u));
        } catch (err: any) {
            console.error('Error updating user apps:', err);
            setError('Erreur lors de la mise à jour des applications');
        } finally {
            setProcessingId(null);
        }
    };

    const handleGrantAllApps = async (userId: string) => {
        const allAppIds = AVAILABLE_APPS.map(app => app.id);
        setProcessingId(userId);
        try {
            await updateUserAllowedApps(userId, allAppIds);
            setUsers(users.map(u => u.uid === userId ? { ...u, allowedApps: allAppIds } : u));
        } catch (err: any) {
            console.error('Error granting all apps:', err);
            setError('Erreur lors de l\'attribution des applications');
        } finally {
            setProcessingId(null);
        }
    };

    const handleRevokeAllApps = async (userId: string) => {
        setProcessingId(userId);
        try {
            await updateUserAllowedApps(userId, []);
            setUsers(users.map(u => u.uid === userId ? { ...u, allowedApps: [] } : u));
        } catch (err: any) {
            console.error('Error revoking all apps:', err);
            setError('Erreur lors de la révocation des applications');
        } finally {
            setProcessingId(null);
        }
    };

    const filteredUsers = users.filter((user) => {
        // Don't show the current user
        if (user.uid === userProfile?.uid) return false;

        // Filter by approval status
        if (filter === 'pending' && user.isApproved) return false;
        if (filter === 'approved' && !user.isApproved) return false;

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                user.email.toLowerCase().includes(query) ||
                user.displayName.toLowerCase().includes(query)
            );
        }

        return true;
    });

    const pendingCount = users.filter(u => !u.isApproved && u.uid !== userProfile?.uid).length;

    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const toggleExpand = (userId: string) => {
        setExpandedUserId(expandedUserId === userId ? null : userId);
    };

    return (
        <div className="admin-users-page animate-fadeIn">
            <header className="page-header">
                <div>
                    <h1 className="page-title">Gestion des utilisateurs</h1>
                    <p className="page-subtitle">
                        Approuvez ou révoquez l'accès des utilisateurs au service
                        {pendingCount > 0 && (
                            <span className="pending-badge">{pendingCount} en attente</span>
                        )}
                    </p>
                </div>
            </header>

            <div className="users-toolbar glass">
                <Input
                    placeholder="Rechercher un utilisateur..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    leftIcon={<Search size={18} />}
                    className="search-input"
                />

                <div className="filter-buttons">
                    <button
                        className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        <Filter size={14} />
                        Tous ({users.length - 1})
                    </button>
                    <button
                        className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
                        onClick={() => setFilter('pending')}
                    >
                        En attente ({pendingCount})
                    </button>
                    <button
                        className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
                        onClick={() => setFilter('approved')}
                    >
                        Approuvés ({users.filter(u => u.isApproved && u.uid !== userProfile?.uid).length})
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className="loading-state">
                    <Loader2 size={32} className="spin" />
                    <p>Chargement des utilisateurs...</p>
                </div>
            ) : error && users.length === 0 ? (
                <div className="error-state glass">
                    <p>{error}</p>
                    <Button variant="secondary" onClick={loadUsers}>Réessayer</Button>
                </div>
            ) : filteredUsers.length === 0 ? (
                <div className="empty-state glass">
                    <Users size={64} className="empty-icon" />
                    <h2>{searchQuery ? 'Aucun résultat' : 'Aucun utilisateur'}</h2>
                    <p>
                        {searchQuery
                            ? 'Aucun utilisateur ne correspond à votre recherche.'
                            : filter === 'pending'
                                ? 'Aucun utilisateur en attente d\'approbation.'
                                : 'Aucun utilisateur inscrit pour le moment.'}
                    </p>
                </div>
            ) : (
                <div className="users-list">
                    {filteredUsers.map((user) => (
                        <div key={user.uid} className="user-card glass">
                            <div className="user-card-main" onClick={() => toggleExpand(user.uid)}>
                                <div className="user-info-section">
                                    <div className="user-avatar-large">
                                        {user.displayName?.charAt(0).toUpperCase() || 'U'}
                                    </div>
                                    <div className="user-details-section">
                                        <span className="user-name-large">{user.displayName}</span>
                                        <span className="user-email-text">{user.email}</span>
                                        <span className="user-date-text">Inscrit le {formatDate(user.createdAt)}</span>
                                    </div>
                                </div>

                                <div className="user-badges">
                                    <span className={`role-badge role-${user.role}`}>
                                        {user.role === 'admin' ? 'Admin' : 'Utilisateur'}
                                    </span>
                                    <span className={`status-badge ${user.isApproved ? 'approved' : 'pending'}`}>
                                        {user.isApproved ? 'Approuvé' : 'En attente'}
                                    </span>
                                    <span className="apps-count-badge">
                                        <AppWindow size={14} />
                                        {(user.allowedApps?.length || 0)} app{(user.allowedApps?.length || 0) !== 1 ? 's' : ''}
                                    </span>
                                </div>

                                <div className="user-actions-main">
                                    {processingId === user.uid ? (
                                        <Loader2 size={18} className="spin" />
                                    ) : deleteConfirmId === user.uid ? (
                                        <div className="delete-confirm">
                                            <span>Supprimer ?</span>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={(e) => { e.stopPropagation(); handleDelete(user.uid); }}
                                                className="delete-confirm-btn"
                                            >
                                                Oui
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(null); }}
                                            >
                                                Non
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="action-buttons">
                                            {user.isApproved ? (
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    leftIcon={<X size={14} />}
                                                    onClick={(e) => { e.stopPropagation(); handleRevoke(user.uid); }}
                                                    className="revoke-btn"
                                                >
                                                    Révoquer
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    leftIcon={<Check size={14} />}
                                                    onClick={(e) => { e.stopPropagation(); handleApprove(user.uid); }}
                                                    className="approve-btn"
                                                >
                                                    Approuver
                                                </Button>
                                            )}
                                            <button
                                                className="delete-btn"
                                                onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(user.uid); }}
                                                title="Supprimer l'utilisateur"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                            <button className="expand-btn">
                                                {expandedUserId === user.uid ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {expandedUserId === user.uid && (
                                <div className="user-card-expanded">
                                    <div className="apps-section">
                                        <div className="apps-header">
                                            <h4><AppWindow size={16} /> Applications autorisées</h4>
                                            <div className="apps-actions">
                                                <button
                                                    className="apps-action-btn grant-all"
                                                    onClick={() => handleGrantAllApps(user.uid)}
                                                    disabled={processingId === user.uid}
                                                >
                                                    <Check size={14} /> Tout autoriser
                                                </button>
                                                <button
                                                    className="apps-action-btn revoke-all"
                                                    onClick={() => handleRevokeAllApps(user.uid)}
                                                    disabled={processingId === user.uid}
                                                >
                                                    <X size={14} /> Tout révoquer
                                                </button>
                                            </div>
                                        </div>
                                        <div className="apps-grid">
                                            {AVAILABLE_APPS.map(app => {
                                                const isAllowed = user.allowedApps?.includes(app.id) || false;
                                                return (
                                                    <label key={app.id} className={`app-checkbox ${isAllowed ? 'checked' : ''}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isAllowed}
                                                            onChange={() => handleToggleApp(user.uid, app.id)}
                                                            disabled={processingId === user.uid}
                                                        />
                                                        <span className="app-icon">{app.icon}</span>
                                                        <span className="app-name">{app.name}</span>
                                                        <span className={`app-status ${isAllowed ? 'allowed' : 'denied'}`}>
                                                            {isAllowed ? <Check size={14} /> : <X size={14} />}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {error && users.length > 0 && (
                <div className="error-toast">{error}</div>
            )}
        </div>
    );
}
